import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Bot,
  UserPlus,
  Search,
  Clock,
  Zap,
  MessageSquare,
  Star,
  Sparkles,
  Plus,
  X,
} from "lucide-react";
import { AppHeader } from "@/components/sartn/AppHeader";
import { useAutomationStore } from "@/stores/automationStore";
import type {
  AutomationType,
  AutomationScope,
  TriggerConfig,
  ActionConfig,
  ScheduleConfig,
} from "@/stores/automationStore";
import { cn } from "@/lib/cn";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const SLIDE_VARIANTS = {
  enter: (dir: number) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -120 : 120, opacity: 0 }),
};

interface TypeOption {
  type: AutomationType;
  label: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  color: string;
}

const TYPE_OPTIONS: TypeOption[] = [
  { type: "auto_reply", label: "Auto Reply", description: "Reply automatically to messages", icon: Bot, color: "bg-blue-50 text-blue-700" },
  { type: "welcome", label: "Welcome", description: "Greet new contacts", icon: UserPlus, color: "bg-green-50 text-green-700" },
  { type: "keyword", label: "Keyword", description: "Trigger on specific words", icon: Search, color: "bg-purple-50 text-purple-700" },
  { type: "scheduled", label: "Scheduled", description: "Run at specific times", icon: Clock, color: "bg-amber-50 text-amber-700" },
  { type: "event", label: "Event", description: "React to business events", icon: Zap, color: "bg-red-50 text-red-700" },
  { type: "follow_up", label: "Follow-Up", description: "Follow up after delay", icon: MessageSquare, color: "bg-cyan-50 text-cyan-700" },
  { type: "feedback", label: "Feedback", description: "Request customer feedback", icon: Star, color: "bg-yellow-50 text-yellow-700" },
];

const DAYS = [
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
  { key: "sun", label: "S" },
];

const EVENTS = [
  { value: "payment_confirmed", label: "Payment Confirmed" },
  { value: "order_placed", label: "Order Placed" },
  { value: "order_completed", label: "Order Completed" },
  { value: "repeat_purchase", label: "Repeat Purchase" },
];

const SCOPE_OPTIONS: { value: AutomationScope; label: string }[] = [
  { value: "all", label: "All Chats" },
  { value: "private", label: "Private Only" },
  { value: "group", label: "Groups Only" },
  { value: "channel", label: "Channels Only" },
];

const TOTAL_STEPS = 4;
const DEFAULT_ACTIVE_HOURS = { start: "09:00", end: "18:00" };
const DEFAULT_ACTIVE_DAYS = ["mon", "tue", "wed", "thu", "fri"];

export function AutomationWizard() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [selectedType, setSelectedType] = useState<AutomationType | null>(null);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<AutomationScope>("all");
  const [triggerConfig, setTriggerConfig] = useState<TriggerConfig>({ type: "message" });
  const [actionConfig, setActionConfig] = useState<ActionConfig>({ type: "send_message", language: "auto" });
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({});
  const [keywordInput, setKeywordInput] = useState("");

  const { createAutomation, templates, createFromTemplate, loadTemplates } = useAutomationStore();

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  function goNext() {
    if (step < TOTAL_STEPS) {
      setDirection(1);
      setStep(step + 1);
    }
  }

  function goBack() {
    if (step > 1) {
      setDirection(-1);
      setStep(step - 1);
    } else {
      window.location.hash = "/automations";
    }
  }

  function handleTypeSelect(type: AutomationType) {
    setSelectedType(type);
    const typeOption = TYPE_OPTIONS.find((t) => t.type === type);
    if (!name) setName(typeOption?.label ?? "");

    switch (type) {
      case "keyword":
        setTriggerConfig({ type: "keyword", keywords: [], keywordMatchMode: "contains" });
        break;
      case "scheduled":
      case "auto_reply":
        setTriggerConfig({ type: "schedule" });
        setScheduleConfig({
          activeHours: DEFAULT_ACTIVE_HOURS,
          activeDays: DEFAULT_ACTIVE_DAYS,
          timezone: "Africa/Addis_Ababa",
          outsideHoursOnly: type === "auto_reply",
        });
        break;
      case "event":
        setTriggerConfig({ type: "event", eventName: "payment_confirmed" });
        break;
      case "follow_up":
        setTriggerConfig({ type: "no_reply", delayMinutes: 2880 });
        break;
      case "feedback":
        setTriggerConfig({ type: "no_reply", delayMinutes: 10080 });
        break;
      case "welcome":
        setTriggerConfig({ type: "new_contact" });
        break;
    }
    goNext();
  }

  async function handleTemplateSelect(templateId: number) {
    await createFromTemplate(templateId);
    window.location.hash = "/automations";
  }

  function addKeyword() {
    const trimmed = keywordInput.trim();
    if (trimmed && !(triggerConfig.keywords ?? []).includes(trimmed)) {
      setTriggerConfig({ ...triggerConfig, keywords: [...(triggerConfig.keywords ?? []), trimmed] });
      setKeywordInput("");
    }
  }

  function removeKeyword(kw: string) {
    setTriggerConfig({ ...triggerConfig, keywords: (triggerConfig.keywords ?? []).filter((k) => k !== kw) });
  }

  function toggleDay(day: string) {
    const current = scheduleConfig.activeDays ?? [];
    const updated = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    setScheduleConfig({ ...scheduleConfig, activeDays: updated.length > 0 ? updated : undefined });
  }

  function insertVariable(varName: string) {
    setActionConfig({ ...actionConfig, message: (actionConfig.message ?? "") + `{{${varName}}}` });
  }

  async function handleCreate() {
    if (!selectedType || !name.trim()) return;
    await createAutomation({
      name: name.trim(),
      description: "",
      automationType: selectedType,
      enabled: true,
      priority: 10,
      scope,
      targetCategories: [],
      triggerConfig,
      actionConfig,
      scheduleConfig,
    });
    window.location.hash = "/automations";
  }

  const canProceed = step === 1 ? selectedType !== null : step === 3 ? (actionConfig.message ?? "").length > 0 : true;

  return (
    <div className="flex min-h-dvh flex-col bg-[var(--color-bg)]">
      <AppHeader title="New Automation" onBack={goBack} />

      <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

      <div className="flex-1 overflow-y-auto px-4 pb-28">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ ...SPRING, duration: 0.25 }}
          >
            {step === 1 && <StepChooseType onSelect={handleTypeSelect} templates={templates} onTemplateSelect={handleTemplateSelect} />}
            {step === 2 && <StepConfigureTrigger selectedType={selectedType!} triggerConfig={triggerConfig} setTriggerConfig={setTriggerConfig} scheduleConfig={scheduleConfig} setScheduleConfig={setScheduleConfig} keywordInput={keywordInput} setKeywordInput={setKeywordInput} addKeyword={addKeyword} removeKeyword={removeKeyword} toggleDay={toggleDay} />}
            {step === 3 && <StepConfigureAction actionConfig={actionConfig} setActionConfig={setActionConfig} scope={scope} setScope={setScope} insertVariable={insertVariable} />}
            {step === 4 && <StepReview name={name} setName={setName} selectedType={selectedType!} scope={scope} triggerConfig={triggerConfig} actionConfig={actionConfig} scheduleConfig={scheduleConfig} />}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="fixed bottom-0 left-0 right-0 border-t border-[var(--color-border)] bg-[var(--color-bg)]/95 px-4 py-3 backdrop-blur-sm">
        <div className="flex gap-3">
          <motion.button whileTap={{ scale: 0.95 }} onClick={goBack} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-secondary)]">
            <ArrowLeft className="h-4 w-4" /> Back
          </motion.button>
          {step < TOTAL_STEPS ? (
            <motion.button whileTap={{ scale: 0.95 }} onClick={goNext} disabled={!canProceed} className={cn("flex h-12 flex-1 items-center justify-center gap-2 rounded-xl text-sm font-medium", canProceed ? "bg-blue-600 text-white" : "bg-[var(--color-surface)] text-[var(--color-text-muted)]")}>
              Next <ArrowRight className="h-4 w-4" />
            </motion.button>
          ) : (
            <motion.button whileTap={{ scale: 0.95 }} onClick={handleCreate} className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-medium text-white">
              <Check className="h-4 w-4" /> Create
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepIndicator({ currentStep, totalSteps }: { currentStep: number; totalSteps: number }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-3">
      {Array.from({ length: totalSteps }, (_, i) => (
        <motion.div key={i} className={cn("h-1.5 rounded-full", i + 1 <= currentStep ? "bg-blue-500" : "bg-[var(--color-surface-raised)]")} animate={{ width: i + 1 === currentStep ? 24 : 12 }} transition={SPRING} />
      ))}
    </div>
  );
}

function StepChooseType({ onSelect, templates, onTemplateSelect }: { onSelect: (t: AutomationType) => void; templates: { id: number; name: string; nameAm: string; automationType: AutomationType }[]; onTemplateSelect: (id: number) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-headline text-lg font-semibold text-[var(--color-text)]">Choose Type</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">What should this automation do?</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {TYPE_OPTIONS.map((opt, i) => {
          const Icon = opt.icon;
          return (
            <motion.button key={opt.type} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} whileTap={{ scale: 0.96 }} onClick={() => onSelect(opt.type)} className="flex flex-col items-start gap-2 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-4 text-left">
              <div className={cn("flex h-11 w-11 items-center justify-center rounded-lg", opt.color)}><Icon className="h-5 w-5" /></div>
              <span className="text-sm font-medium text-[var(--color-text)]">{opt.label}</span>
              <span className="text-xs text-[var(--color-text-muted)]">{opt.description}</span>
            </motion.button>
          );
        })}
      </div>

      {templates.length > 0 && (
        <div>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-[var(--color-text-secondary)]">
            <Sparkles className="h-4 w-4 text-amber-600" /> Or start from a template
          </h3>
          <div className="space-y-2">
            {templates.slice(0, 4).map((tpl) => (
              <motion.button key={tpl.id} whileTap={{ scale: 0.98 }} onClick={() => onTemplateSelect(tpl.id)} className="flex w-full items-center gap-3 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-4 py-3 text-left">
                <span className="text-sm font-medium text-[var(--color-text)]">{tpl.name}</span>
                <span className="ml-auto text-xs text-[var(--color-text-muted)]">{tpl.nameAm}</span>
              </motion.button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StepConfigureTrigger({ selectedType, triggerConfig, setTriggerConfig, scheduleConfig, setScheduleConfig, keywordInput, setKeywordInput, addKeyword, removeKeyword, toggleDay }: {
  selectedType: AutomationType; triggerConfig: TriggerConfig; setTriggerConfig: (c: TriggerConfig) => void; scheduleConfig: ScheduleConfig; setScheduleConfig: (c: ScheduleConfig) => void; keywordInput: string; setKeywordInput: (v: string) => void; addKeyword: () => void; removeKeyword: (kw: string) => void; toggleDay: (d: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-headline text-lg font-semibold text-[var(--color-text)]">Configure Trigger</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">When should this automation activate?</p>
      </div>

      {selectedType === "keyword" && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-[var(--color-text-secondary)]">Keywords</label>
          <div className="flex gap-2">
            <input value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addKeyword()} placeholder="Add keyword..." className="flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] outline-none focus:border-blue-500" />
            <motion.button whileTap={{ scale: 0.9 }} onClick={addKeyword} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-600 active:scale-95"><Plus className="h-5 w-5 text-white" /></motion.button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(triggerConfig.keywords ?? []).map((kw) => (
              <span key={kw} className="flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs text-blue-700">
                {kw}
                <button onClick={() => removeKeyword(kw)} className="flex h-6 w-6 items-center justify-center rounded-full active:scale-90 active:bg-blue-100"><X className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--color-text-secondary)]">Match Mode</label>
            <div className="flex gap-2">
              {(["contains", "exact", "regex"] as const).map((mode) => (
                <button key={mode} onClick={() => setTriggerConfig({ ...triggerConfig, keywordMatchMode: mode })} className={cn("rounded-lg px-4 py-2.5 text-xs font-medium active:scale-95", triggerConfig.keywordMatchMode === mode ? "bg-blue-600 text-white" : "bg-[var(--color-surface)] text-[var(--color-text-muted)]")}>
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {(selectedType === "scheduled" || selectedType === "auto_reply") && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--color-text-secondary)]">Active Hours</label>
            <div className="flex items-center gap-3">
              <input type="time" value={scheduleConfig.activeHours?.start ?? DEFAULT_ACTIVE_HOURS.start} onChange={(e) => setScheduleConfig({ ...scheduleConfig, activeHours: { start: e.target.value, end: scheduleConfig.activeHours?.end ?? DEFAULT_ACTIVE_HOURS.end } })} className="w-28 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-center text-sm text-[var(--color-text)] outline-none focus:border-blue-500" />
              <span className="text-sm text-[var(--color-text-muted)]">to</span>
              <input type="time" value={scheduleConfig.activeHours?.end ?? DEFAULT_ACTIVE_HOURS.end} onChange={(e) => setScheduleConfig({ ...scheduleConfig, activeHours: { start: scheduleConfig.activeHours?.start ?? DEFAULT_ACTIVE_HOURS.start, end: e.target.value } })} className="w-28 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-center text-sm text-[var(--color-text)] outline-none focus:border-blue-500" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--color-text-secondary)]">Active Days</label>
            <div className="flex gap-2">
              {DAYS.map((day) => (
                <button key={day.key} onClick={() => toggleDay(day.key)} className={cn("flex h-11 w-11 items-center justify-center rounded-lg text-sm font-medium active:scale-95", (scheduleConfig.activeDays ?? []).includes(day.key) ? "bg-blue-600 text-white" : "bg-[var(--color-surface)] text-[var(--color-text-muted)]")}>
                  {day.label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={scheduleConfig.outsideHoursOnly ?? false} onChange={(e) => setScheduleConfig({ ...scheduleConfig, outsideHoursOnly: e.target.checked })} className="h-5 w-5 rounded border-gray-300 bg-[var(--color-surface)]" />
            <span className="text-sm text-[var(--color-text-secondary)]">Only trigger outside active hours</span>
          </label>
        </div>
      )}

      {selectedType === "event" && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-text-secondary)]">Event Type</label>
          <div className="space-y-2">
            {EVENTS.map((ev) => (
              <button key={ev.value} onClick={() => setTriggerConfig({ ...triggerConfig, eventName: ev.value })} className={cn("flex w-full items-center rounded-xl border px-4 py-3 text-left text-sm active:scale-[0.98]", triggerConfig.eventName === ev.value ? "border-blue-500 bg-blue-50 text-[var(--color-text)]" : "border-[var(--color-border-subtle)] bg-[var(--color-surface)] text-[var(--color-text-secondary)]")}>
                {ev.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {(selectedType === "follow_up" || selectedType === "feedback") && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-[var(--color-text-secondary)]">Delay (days)</label>
          <input type="number" min={1} max={90} value={Math.round((triggerConfig.delayMinutes ?? 2880) / 1440)} onChange={(e) => setTriggerConfig({ ...triggerConfig, delayMinutes: Number(e.target.value) * 1440 })} className="w-24 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-sm text-[var(--color-text)] outline-none focus:border-blue-500" />
        </div>
      )}

      {selectedType === "welcome" && (
        <div className="rounded-xl bg-green-50 p-4">
          <p className="text-sm text-green-700">This automation triggers when someone messages you for the first time.</p>
        </div>
      )}
    </div>
  );
}

function StepConfigureAction({ actionConfig, setActionConfig, scope, setScope, insertVariable }: {
  actionConfig: ActionConfig; setActionConfig: (c: ActionConfig) => void; scope: AutomationScope; setScope: (s: AutomationScope) => void; insertVariable: (v: string) => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-headline text-lg font-semibold text-[var(--color-text)]">Configure Action</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">What happens when this automation triggers?</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-text-secondary)]">Message (English)</label>
        <textarea value={actionConfig.message ?? ""} onChange={(e) => setActionConfig({ ...actionConfig, message: e.target.value })} rows={3} placeholder="Type your auto-reply message..." className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] outline-none focus:border-blue-500" />
        <div className="flex flex-wrap gap-1.5">
          {["name", "product", "date", "business_name"].map((v) => (
            <button key={v} onClick={() => insertVariable(v)} className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700 active:scale-95 active:bg-blue-100">{`{{${v}}}`}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-text-secondary)]">Message (Amharic — optional)</label>
        <textarea value={actionConfig.messageAmharic ?? ""} onChange={(e) => setActionConfig({ ...actionConfig, messageAmharic: e.target.value })} rows={2} placeholder="በአማርኛ..." className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] outline-none focus:border-blue-500" />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-text-secondary)]">Scope</label>
        <div className="grid grid-cols-2 gap-2">
          {SCOPE_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => setScope(opt.value)} className={cn("rounded-xl border px-3 py-2.5 text-sm active:scale-[0.97]", scope === opt.value ? "border-blue-500 bg-blue-50 font-medium text-[var(--color-text)]" : "border-[var(--color-border-subtle)] bg-[var(--color-surface)] text-[var(--color-text-muted)]")}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-3">
        <input type="checkbox" checked={actionConfig.aiEnabled ?? false} onChange={(e) => setActionConfig({ ...actionConfig, aiEnabled: e.target.checked })} className="h-5 w-5 rounded border-gray-300 bg-[var(--color-surface)]" />
        <span className="text-sm text-[var(--color-text-secondary)]">AI-enhanced responses</span>
        <Sparkles className="h-3.5 w-3.5 text-amber-600" />
      </label>
    </div>
  );
}

function StepReview({ name, setName, selectedType, scope, triggerConfig, actionConfig, scheduleConfig }: {
  name: string; setName: (n: string) => void; selectedType: AutomationType; scope: AutomationScope; triggerConfig: TriggerConfig; actionConfig: ActionConfig; scheduleConfig: ScheduleConfig;
}) {
  const typeOption = TYPE_OPTIONS.find((t) => t.type === selectedType);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-headline text-lg font-semibold text-[var(--color-text)]">Review</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">Confirm your automation settings.</p>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-[var(--color-text-secondary)]">Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-2.5 text-sm text-[var(--color-text)] outline-none focus:border-blue-500" />
      </div>

      <div className="space-y-3">
        <ReviewRow label="Type" value={typeOption?.label ?? selectedType} />
        <ReviewRow label="Scope" value={scope} />
        <ReviewRow label="Trigger" value={formatTrigger(triggerConfig)} />
        {(triggerConfig.keywords ?? []).length > 0 && <ReviewRow label="Keywords" value={(triggerConfig.keywords ?? []).join(", ")} />}
        {scheduleConfig.activeHours && (
          <ReviewRow
            label="Hours"
            value={`${scheduleConfig.activeHours.start} - ${scheduleConfig.activeHours.end}`}
          />
        )}
        <ReviewRow label="AI Enhanced" value={actionConfig.aiEnabled ? "Yes" : "No"} />
        {actionConfig.message && (
          <div className="rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] p-3">
            <span className="text-xs text-[var(--color-text-muted)]">Message</span>
            <p className="mt-1 text-sm text-[var(--color-text)]">{actionConfig.message}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-surface)] px-4 py-3">
      <span className="text-sm text-[var(--color-text-muted)]">{label}</span>
      <span className="text-sm font-medium text-[var(--color-text)]">{value}</span>
    </div>
  );
}

function formatTrigger(config: TriggerConfig): string {
  switch (config.type) {
    case "keyword": return "Keyword match";
    case "schedule": return "Schedule-based";
    case "event": return config.eventName ?? "Event";
    case "no_reply": return `${Math.round((config.delayMinutes ?? 0) / 1440)} day no-reply delay`;
    case "new_contact": return "New contact";
    case "message": return "Every message";
    default: return config.type;
  }
}

export default AutomationWizard;
